import { Component, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AsyncPipe, DOCUMENT } from '@angular/common';
import { ThemeConfig } from '../assets/data/enum/themeConfig';
import { TakeLanguageOfBrowserService } from './common/_services/takeLanguageOfBrowser.service';
import { GoogleTagManagerService } from './common/_services/google/angular-google-tag-manager.service';
import { environment } from '../environments/environment';
import { ThemeService } from './common/_theme/theme.service';
import { User } from './common/_models';
import { SUPPORTED_LANGUAGES } from './config/locale.config';
import { LocaleService } from './common/_services/locale.service';
import { PublicChatSupportModule } from './shared/public-chat-support/public-chat-support.module';
import { DownloadAppOverlayModule } from './shared/download-app-overlay/download-app-overlay.module';
import { XInformationBarModule } from './shared/x-information-bar/x-information-bar.module';
import { AddDocumentationLinkDirective } from './directive/add-documentation-link.directive';
import { CookieBannerComponent } from './cookie-banner/cookie-banner.component';
import { isLocalhost } from '../main';
import { setRoutingLoaderService } from './common/_factory/routing-loader.factory';
import { RoutingUtilitiesService } from './common/_services/routingUtlities.service';
import { SplashScreenPageComponent } from './shared/splash-screen-page/splash-screen-page.component';
import { SAASBRANDNAME } from '../assets/data/enum/saasBrandName.enum';
import { GeneralUtilsService } from './common/_services';

declare let gtag: Function;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: true,
  imports: [
    RouterOutlet,
    PublicChatSupportModule,
    XInformationBarModule,
    DownloadAppOverlayModule,
    AddDocumentationLinkDirective,
    CookieBannerComponent,
    SplashScreenPageComponent,
    AsyncPipe,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  public router = inject(Router);
  private translate = inject(TranslateService);
  private takeLanguageOfBrowserService = inject(TakeLanguageOfBrowserService);
  private gtmService = inject(GoogleTagManagerService);
  private themeService = inject(ThemeService);
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  private localeService = inject(LocaleService);
  private routingUtilitiesService = inject(RoutingUtilitiesService);
  private generalUtilsService = inject(GeneralUtilsService);

  private subscriptions: Subscription = new Subscription();
  themeConfig = ThemeConfig;
  isMobile = false;
  user: User = new User();
  scriptsTranslateLoaded = false;
  environment = environment;
  showSplashScreenComponent$ = this.generalUtilsService.showSplashScreenComponent$;

  ngOnInit() {
    // Set google tag manager and google analytics
    this.setGoogleTagManager();
    this.setGoogleAnalytics();
    this.startRouteTrackToGoogleAnalytics();
    this.loadTranslateScripts();
    setRoutingLoaderService(this.routingUtilitiesService);

    // Set version from environment
    this.themeService.getVersion().subscribe();

    // Set Hello Developer
    if (this.themeConfig.brand.toLowerCase() == SAASBRANDNAME.XFARM.toLowerCase()) {
      this.setHelloDeveloper();
    }

    // Disable console log in environment.production
    if (environment.production && !isLocalhost) {
      window.console.log = () => {};
      window.console.warn = () => {};
    }

    // Set the theme colors
    this.themeService.setTheme(this.themeService.getThemeBySaas().theme);

    // Set title tag
    this.themeService.setTitleTagHeader();

    // Set icon metadata in header
    this.themeService.setLinkRelsInHeader();

    // Set header meta tags
    this.themeService.setMetaTAGsInHeader();

    // Setting ng-translate available languages
    const languages = SUPPORTED_LANGUAGES;
    if (environment.enableTranslateMode) {
      languages.push('ach');
    }
    this.translate.addLangs(languages);

    // Setting the default language from the user browser
    const defaultLang = this.takeLanguageOfBrowserService.getLanguage();
    this.translate.setDefaultLang(defaultLang);

    // Search for a language stored in the local storage
    const storedLng = localStorage.getItem('crntLng');
    const langToUse =
      storedLng && SUPPORTED_LANGUAGES.includes(storedLng) ? storedLng : defaultLang;

    this.translate.use(langToUse);
    this.localeService.setLocale(langToUse);
    localStorage.setItem('crntLng', langToUse);
  }

  setGoogleTagManager() {
    const script = this.renderer.createElement('script');
    this.renderer.setAttribute(
      script,
      'src',
      `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsId}`
    );
    this.renderer.setAttribute(script, 'async', 'true');
    this.renderer.appendChild(this.document.head, script);
  }

  setGoogleAnalytics() {
    // Crea il tag di script personalizzato con la variabile
    const customScript = this.renderer.createElement('script');
    customScript.text = `
      window.dataLayer = window.dataLayer || []
      function gtag(){dataLayer.push(arguments)}
      gtag('js', new Date())
      gtag('config', '${environment.googleAnalyticsId}')
    `;
    // Aggiungi il tag di script personalizzato al tag <head> del documento
    this.renderer.appendChild(this.document.head, customScript);
  }

  startRouteTrackToGoogleAnalytics() {
    this.subscriptions.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd && event.url) {
          const gtmTag = {
            event: 'page',
            pageName: event.url,
          };
          this.gtmService.pushTag(gtmTag);
          gtag('config', environment.googleAnalyticsId, { page_path: event.url });
        }
      })
    );
  }

  setHelloDeveloper = () => {
    // check browser isChrome
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);

    // UI text into console.log()
    const styleStartEnd =
      'background-color: #608e63; color: white; border: 2px solid #608e63; font-size: 1.5em;';
    const styleHello = 'background-color: #608e63; color: white; font-size: 1.5em;';
    const styleText = 'color: white; font-size: 1.2em;';

    ////////// START PRINT ////////////
    console.log('%c 🚜 💨💨💨💨💨💨💨💨💨💨💨', styleStartEnd);

    // the xFarm logo is available only for chrome //
    if (isChrome) {
      console.log(`
        ██╗  ██╗███████╗ █████╗ ██████╗ ███╗   ███╗
        ╚██╗██╔╝██╔════╝██╔══██╗██╔══██╗████╗ ████║
         ╚███╔╝ █████╗  ███████║██████╔╝██╔████╔██║
         ██╔██╗ ██╔══╝  ██╔══██║██╔══██╗██║╚██╔╝██║
        ██╔╝ ██╗██║     ██║  ██║██║  ██║██║ ╚═╝ ██║
        ╚═╝  ╚═╝╚═╝     ╚═╝  ╚═╝╚═╝  ╚═╝╚═╝     ╚═╝
        `);
    }
    // this point is available only for IT/EN text //
    const lang = localStorage.getItem('crntLng');
    if (lang === 'it') {
      console.log('%cEhi, genio della console! 💻', styleHello);
      console.log(
        "%c🌱 Vuoi programmare il futuro dell'agricoltura con noi? 🚜 Clicca qui: https://xfarm.ag/lavora-con-noi 🚀",
        styleText
      );
    } else {
      console.log('%cHey Man! 💻', styleHello);
      console.log(
        '%c🌱 Do you want to create the future of agriculture with us? 🚜 Click here: https://xfarm.ag/en/works-with-us 🚀',
        styleText
      );
    }

    console.log('%c 🚜 💨💨💨💨💨💨💨💨💨💨💨', styleStartEnd);
    ////////// END PRINT ////////////
  };

  loadTranslateScripts() {
    if (environment.enableTranslateMode != true) {
      return;
    }
    if (this.scriptsTranslateLoaded) {
      return;
    }

    this.scriptsTranslateLoaded = true;

    // First Crowdin script
    const script1 = document.createElement('script');
    script1.type = 'text/javascript';
    script1.text = `
      var _jipt = [];
      _jipt.push(['project', 'c08b38a95009becbd0d096db525d11ac']);
      _jipt.push(['domain', 'xfarm']);
    `;
    document.head.appendChild(script1);

    // Second Crowdin script
    const script2 = document.createElement('script');
    script2.type = 'text/javascript';
    script2.text = `
      var _jipt = [];
      _jipt.push(['project', 'c08b38a95009becbd0d096db525d11ac']);
      _jipt.push(['domain', 'xfarm']);
      _jipt.push(['preload_texts', false]);
      _jipt.push(['edit_strings_context', function(context) {
        var separator = "\\n";
        var lineStart = "* ";
        var contextParts = context.split(separator);
        var linkCount = 0;

        for (var i in contextParts) {
          var line = contextParts[i];
          if (lineStart + window.location.href === line) {
            return context;
          }

          if (line.indexOf(lineStart) === 0) {
            linkCount++;
          }

          if (line === 'And more...') {
            return context;
          }
        }

        if (linkCount < 5) {
          return context + separator + lineStart + window.location.href;
        }

        return context + separator + 'And more...';
      }]);
    `;
    document.head.appendChild(script2);

    // Script with external src Crowdin
    const script3 = document.createElement('script');
    script3.type = 'text/javascript';
    script3.src = '//cdn.crowdin.com/jipt/jipt.js';
    document.head.appendChild(script3);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
